import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import * as duration from 'dayjs/plugin/duration';
import * as isToday from 'dayjs/plugin/isToday';
import * as timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin
import * as utc from 'dayjs/plugin/utc';
import { Subject } from 'rxjs';
import { RepositoryService } from '../baseServices/repository.service';
import { GlobalConstants } from '../constant/constant';
import { TimeRecordActivity, TimeRecordConstants, TimeRecordStatusHierarchy, TimeRecordStatusNumber } from '../constant/time-record-constants';
import { VerifyActivityLogDataDto } from '../dtos/activity-log-service-dtos';
import {
  ApproveTimeRecordDataDto,
  BaseTimeRecordDataDto,
  CloseTimeRecordDataDto,
  CreateApprovedTimeRecordDataDto,
  CreateClosedTimeRecordDataDto,
  CreateOpenTimeRecordDataDto,
  CreateSubmittedTimeRecordDataDto,
  CreateVerifiedTimeRecordDataDto,
  LockTimeRecordDataDto,
  OpenTimeRecordDataDto,
  ReturnTimeRecordDataDto,
  SubmitTimeRecordDataDto,
  UpdateTimeRecordAssignmentDataDto,
  UpdateTimeRecordClocksDataDto,
  VerifyTimeRecordDataDto,
} from '../dtos/time-record-service-dtos';
import { StoreName } from '../entities/StoreName';
import { TimeRecord } from '../entities/timeRecord';
import { TimeRecordStatus } from '../entities/TimeRecordStatus';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { DbKeyType } from '../models/db/db-store';
import { DbTransaction } from '../models/db/db-transaction';
import { Callback, CallbackAddMode, CallbackNames } from '../models/db/db-transaction-callback';
import { ArrayHelper } from '../utils/array-helper';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { Check, EntityHelper } from '../utils/entity-helper';
import {
  ActionValidationException,
  ConflictValidationException,
  PermissionException,
  RecordNotFoundException,
  RecordSoftDeletedException,
  StateValidationException,
  ValidationException,
} from '../utils/exceptions';
import { LaborCategoryService } from './labor-category.service';
import { ProjectService } from './project.service';
import { RoleClaimService } from './role-claim.service';
import { TimeRecordStatusService } from './time-record-status.service';
import { LookupService } from './lookup.service';
import { UserService } from './user.service';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(duration);

@Injectable({
  providedIn: 'root',
})
export class TimeRecordService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<TimeRecord>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  private statuses: TimeRecordStatus[] = [];
  private intervalCacheByFipsStateId = new Map<number, number>();
  private isExemptCacheByEmployeeId = new Map<number, boolean>();
  constructor(
    private repository: RepositoryService,
    private laborCategoryService: LaborCategoryService,
    private userService: UserService,
    private roleClaimService: RoleClaimService,
    private timeRecordStatusService: TimeRecordStatusService,
    private projectService: ProjectService, // TODO: Remove this dependency
    private lookupService: LookupService
  ) {
    this.loadStatuses();
    this.subscribeToUserChanges();
  }

  private subscribeToUserChanges(): void {
    this.userService.changeEmitted$.subscribe(event => {
      const user = event.data;
      if (user?.Id) {
        this.isExemptCacheByEmployeeId.set(user.Id, user.IsExempt ?? true);
      }
    });
  }

  // #region Utility

  /**
   * Loads the statuses for time records into memory.
   * @param existingTransaction - Optional. Database transaction to use.
   * @returns A promise that resolves when the statuses have been loaded.
   */
  async loadStatuses(existingTransaction?: DbTransaction) {
    this.statuses = await this.timeRecordStatusService.getAllTimeRecordStatuses(undefined, existingTransaction);
  }

  emitChange(timeRecord: TimeRecord, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: timeRecord, who, what });
  }

  private getEmitChangeCallback(timeRecord: TimeRecord, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForTimeRecord + '_' + timeRecord.RecordId,
      fn: this.emitChange.bind(this, timeRecord, who, what),
    };
  }

  private getValidateTimeRecordCallback(timeRecord: TimeRecord): Callback {
    return {
      name: CallbackNames.ValidateTimeRecord,
      fn: this.validateTimeRecord.bind(this, timeRecord),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.TimeRecord, StoreName.TimeRecordStatus], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getTimeRecordByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<TimeRecord>(StoreName.TimeRecord, recordId, TimeRecord, transaction);
    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as TimeRecord;

    if (this.checkIsTimeRecordSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  async getTimeRecordByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<TimeRecord>(StoreName.TimeRecord, onlineId, TimeRecord, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as TimeRecord;

    if (this.checkIsTimeRecordSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getTimeRecordsByActivityLogId(activityLogId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecord[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<TimeRecord>(StoreName.TimeRecord, GlobalConstants.KEY_NAMES.ActivityLogId, activityLogId, TimeRecord, transaction);
    let records = result as TimeRecord[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsTimeRecordSoftDeleted(record));
    }

    return records;
  }

  async getTimeRecordsByActivityLogRecordId(activityLogRecordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecord[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex<TimeRecord>(StoreName.TimeRecord, GlobalConstants.KEY_NAMES.ActivityLogRecordId, activityLogRecordId, TimeRecord, transaction);
    let records = result as TimeRecord[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsTimeRecordSoftDeleted(record));
    }

    return records;
  }

  async getTimeRecordsByUserIds(userIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecord[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAllByIndex(StoreName.TimeRecord, GlobalConstants.KEY_NAMES.UserId, userIds, TimeRecord, transaction);
    let records = result as TimeRecord[];

    if (!includeDeleted) {
      records = records.filter(record => !this.checkIsTimeRecordSoftDeleted(record));
    }

    return records;
  }

  async getTimeRecordsByUserIdDate(userId: number, date: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecord[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const startDate = DateTimeHelper.formatDateTime(DateTimeHelper.getStartOfDay(date));
    const endDate = DateTimeHelper.formatDateTime(DateTimeHelper.getEndOfDay(date));
    const key: DbKeyType = {
      lower: [userId, startDate],
      upper: [userId, endDate],
    };

    const result = await this.repository.getAllByIndex(StoreName.TimeRecord, GlobalConstants.KEY_NAMES.UserId_Date, key, TimeRecord, transaction);
    let records = result as TimeRecord[];

    if (!includeDeleted) {
      records = result.filter(TimeRecord => !this.checkIsTimeRecordSoftDeleted(TimeRecord));
    }

    return records;
  }

  async getTimeRecordsByUserIdProjectIdDate(userId: number, projectId: number, date: DateTimeType, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<TimeRecord[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const startDate = DateTimeHelper.formatDateTime(DateTimeHelper.getStartOfDay(date));
    const endDate = DateTimeHelper.formatDateTime(DateTimeHelper.getEndOfDay(date));
    const key: DbKeyType = {
      lower: [userId, projectId, startDate],
      upper: [userId, projectId, endDate],
    };

    const result = await this.repository.getAllByIndex(StoreName.TimeRecord, GlobalConstants.KEY_NAMES.UserId_ProjectId_Date, key, TimeRecord, transaction);

    let records = result as TimeRecord[];

    if (!includeDeleted) {
      records = result.filter(TimeRecord => !this.checkIsTimeRecordSoftDeleted(TimeRecord));
    }

    return records;
  }

  // #endregion
  // #region Low-level Create

  async createTimeRecord(who: DataChangeEmitWho, recordToCreate: TimeRecord, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who === DataChangeEmitWho.Self) {
      // LC1. Validate action
      await this.validateTimeRecordCreation(transaction);

      // LC2. Idempotent check
      const existingRecord = (await this.getTimeRecordsByUserIdProjectIdDate(recordToCreate.EmployeeId, recordToCreate.ProjectId, recordToCreate.ClockIn, transaction)).find(existingRecord =>
        this.checkCoreFieldsMatch(recordToCreate, existingRecord)
      );
      if (existingRecord != null) {
        return existingRecord.RecordId;
      }

      // LC3. Validate model
      await this.validateTimeRecord(recordToCreate, transaction);
    } else {
      transaction.addOnCommitCallback(this.getValidateTimeRecordCallback(recordToCreate), CallbackAddMode.PushDuplicate);
    }

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.TimeRecord, transaction);

    // LC5. Get created record
    const createdRecord = await this.getTimeRecordByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // #region Low-level Update

  async updateTimeRecord(who: DataChangeEmitWho, recordToUpdate: TimeRecord, skipLocalCopyValidation: boolean, localCopy: TimeRecord | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who === DataChangeEmitWho.Self) {
      // LU1. Get existing record
      const existingRecord = await this.getTimeRecordByRecordId(recordToUpdate.RecordId, transaction);

      // LU2. Validate action
      await this.validateTimeRecordUpdate(recordToUpdate, transaction);

      // LU3. Idempotent check
      if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
        return;
      }

      // LU4. Validate local copy
      if (!skipLocalCopyValidation) {
        const checks: Check<TimeRecord>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
        EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
      }

      // LU5. Validate model
      await this.validateTimeRecord(recordToUpdate, transaction);
    } else {
      transaction.addOnCommitCallback(this.getValidateTimeRecordCallback(recordToUpdate), CallbackAddMode.PushDuplicate);
    }

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.TimeRecord, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getTimeRecordByRecordId(recordToUpdate.RecordId, transaction, true);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // #region Low-level Delete

  async softDeleteTimeRecord(who: DataChangeEmitWho, recordId: number, skipLocalCopyValidation: boolean, localCopy: TimeRecord | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LD1. Get existing record
    const recordToSoftDelete = await this.getTimeRecordByRecordId(recordId, transaction, true);

    // LD2. Validate action
    await this.validateTimeRecordSoftDeletion(recordToSoftDelete, transaction);

    // LD3. Idempotent check
    if (this.checkIsTimeRecordSoftDeleted(recordToSoftDelete)) {
      return;
    }

    // LD4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy(localCopy, recordToSoftDelete, checks);
    }

    // LD5. Prepare changes
    recordToSoftDelete.setSoftDeleted();

    // LD6. Validate model
    await this.validateSoftDeletedTimeRecord(recordToSoftDelete, transaction);

    // LD7. Repository call
    await this.repository.update(recordToSoftDelete, StoreName.TimeRecord, transaction);

    // LD8. Get soft deleted record
    const softDeletedRecord = await this.getTimeRecordByRecordId(recordId, transaction, true);

    // LD9. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(softDeletedRecord, who, DataChangeEmitWhat.Delete));

    // LD10. Return
    return;
  }

  public async hardDeleteTimeRecord(who: DataChangeEmitWho, recordId: number, skipLocalCopyValidation: boolean, localCopy: TimeRecord | null, existingTransaction?: DbTransaction) {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // 1. Idempotent check
    try {
      // 2. Get existing record
      const existingRecord = await this.getTimeRecordByRecordId(recordId, transaction, true);

      // 3. Local copy validate
      if (!skipLocalCopyValidation) {
        const checks: Check<TimeRecord>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
        EntityHelper.validateLocalCopy(localCopy, existingRecord, checks);
      }

      // 4. Validate hard delete
      await this.validateTimeRecordHardDeletion(existingRecord, transaction);

      // 5. Repository call
      await this.repository.delete(recordId, StoreName.TimeRecord, transaction);

      // 6. Report changes
      transaction.addOnCommitCallback(this.getEmitChangeCallback(existingRecord, who, DataChangeEmitWhat.Delete));

      // 7. Return
      return;
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        return;
      }
      throw e;
    }
  }

  // #endregion
  // #region Low-level Validate Action

  async validateTimeRecordCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateTimeRecordUpdate(existingRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateTimeRecordSoftDeletion(existingRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Check role claim

    return;
  }

  async validateTimeRecordHardDeletion(existingRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
    // Add other validation checks as necessary
  }

  // #endregion
  // #region Low-level Validate Model

  async validateTimeRecord(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const isTimeRecordSoftDeleted = this.checkIsTimeRecordSoftDeleted(timeRecord);
    if (isTimeRecordSoftDeleted) {
      return;
    } else {
      const overlappingTimeRecords = await this.getOverlappingTimeRecords(timeRecord, transaction);
      if (overlappingTimeRecords.length > 0) {
        throw new ConflictValidationException(overlappingTimeRecords);
      }
    }

    if (!this.checkRestBreakDurationBelowMax(timeRecord)) {
      throw new ValidationException(`A rest break's duration cannot be greater than ${TimeRecordConstants.REST_BREAK_DURATION_MINUTES} minutes.`);
    }

    return;
  }

  async validateSoftDeletedTimeRecord(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!this.checkIsTimeRecordSoftDeleted(timeRecord)) {
      throw new StateValidationException('SoftDeleted', timeRecord);
    }

    return;
  }

  // #endregion
  // #region Low-level Sync

  // #endregion
  // #region Mid-level Get One

  async getLatestOpenTimeRecord(userId: number, existingTransaction?: DbTransaction): Promise<TimeRecord | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const existingTimeRecords = await this.getTimeRecordsByUserIds([userId], transaction);
    const checkIsOpenPromises = existingTimeRecords.map(async timeRecord => ({
      TimeRecord: timeRecord,
      IsOpen: await this.checkIsTimeRecordOpen(timeRecord, transaction),
    }));
    const results = await Promise.all(checkIsOpenPromises);
    const openTimeRecords = results.filter(result => result.IsOpen).map(result => result.TimeRecord);
    return this.filterForLatestTimeRecord(openTimeRecords);
  }

  async getLatestClosedWorkingTimeRecord(userId: number, existingTransaction?: DbTransaction): Promise<TimeRecord | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const existingTimeRecords = await this.getTimeRecordsByUserIds([userId], transaction);
    const results = existingTimeRecords.map(timeRecord => ({
      TimeRecord: timeRecord,
      IsWorking: this.checkIsTimeRecordWorking(timeRecord),
    }));
    const workingTimeRecords = results.filter(result => result.IsWorking).map(result => result.TimeRecord);
    return this.filterForLatestTimeRecord(workingTimeRecords);
  }

  async getOpenTimeRecord(userId: number, date: DateTimeType, existingTransaction?: DbTransaction): Promise<TimeRecord | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const existingTimeRecords = await this.getTimeRecordsByUserIdDate(userId, date, transaction);
    const checkIsOpenPromises = existingTimeRecords.map(async timeRecord => ({
      TimeRecord: timeRecord,
      IsOpen: await this.checkIsTimeRecordOpen(timeRecord, transaction),
    }));
    const results = await Promise.all(checkIsOpenPromises);
    const openTimeRecords = results.filter(result => result.IsOpen).map(result => result.TimeRecord);

    if (openTimeRecords.length > 1) {
      console.warn(`Multiple open time records found for user ${userId} on date ${date}. Returning the first one.`);
    }

    return ArrayHelper.firstOrDefault(openTimeRecords);
  }

  async getTimeRecordEnclosingDatetime(userId: number, targetDatetime: DateTimeType, targetTimeZoneId?: string, existingTransaction?: DbTransaction): Promise<TimeRecord | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const timeRecords = await this.getTimeRecordsByUserIdDate(userId, targetDatetime, transaction);
    const enclosingTimeRecords = timeRecords.filter(timeRecord => this.checkDatetimeInTimeRecord(timeRecord, targetDatetime, targetTimeZoneId));

    if (enclosingTimeRecords && enclosingTimeRecords.length === 1) {
      return enclosingTimeRecords[0];
    } else if (enclosingTimeRecords && enclosingTimeRecords.length === 0) {
      return null;
    } else if (enclosingTimeRecords && enclosingTimeRecords.length > 1) {
      throw Error(`Multiple time records enclosing ${dayjs(targetDatetime, targetTimeZoneId).toString()}. Time records cannot overlap.`);
    } else {
      throw Error(`Error getting time record enclosing ${dayjs(targetDatetime, targetTimeZoneId).toString()}.`);
    }
  }

  async getWorkingTimeRecordEnclosingDatetime(userId: number, targetDatetime: DateTimeType, targetTimeZoneId?: string, existingTransaction?: DbTransaction): Promise<TimeRecord | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const enclosingTimeRecord = await this.getTimeRecordEnclosingDatetime(userId, targetDatetime, targetTimeZoneId, transaction);

    if (enclosingTimeRecord) {
      const isWorking = this.checkIsTimeRecordWorking(enclosingTimeRecord);
      return isWorking ? enclosingTimeRecord : null;
    } else {
      return null;
    }
  }

  async getRestTimeRecordEnclosingDatetime(userId: number, targetDatetime: DateTimeType, targetTimeZoneId?: string, existingTransaction?: DbTransaction): Promise<TimeRecord | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const enclosingTimeRecord = await this.getTimeRecordEnclosingDatetime(userId, targetDatetime, targetTimeZoneId, transaction);

    if (enclosingTimeRecord) {
      const isRest = this.checkIsTimeRecordRestBreak(enclosingTimeRecord);
      return isRest ? enclosingTimeRecord : null;
    } else {
      return null;
    }
  }

  async getLastestTimeRecordByUserIdDate(userId: number, date: DateTimeType, existingTransaction?: DbTransaction): Promise<TimeRecord | null> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const timeRecords = await this.getTimeRecordsByUserIdDate(userId, date, transaction);
    const lastestTimeRecord = this.filterForLatestTimeRecord(timeRecords);

    return lastestTimeRecord;
  }

  // #endregion
  // #region Mid-level Get Many

  async getOverlappingTimeRecords(targetTimeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<TimeRecord[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const timeRecordsForUserAndDate = await this.getTimeRecordsByUserIdDate(targetTimeRecord.EmployeeId, targetTimeRecord.Date, transaction);
    let overlappingTimeRecords: TimeRecord[];

    overlappingTimeRecords = timeRecordsForUserAndDate.filter(
      existingTimeRecord => !this.checkAnyTimeRecordIdentifiersMatch(targetTimeRecord, existingTimeRecord) && this.checkTimeRecordsOverlap(targetTimeRecord, existingTimeRecord)
    );

    return overlappingTimeRecords;
  }

  // #endregion
  // #region Mid-level Create

  async createOpenTimeRecord(who: DataChangeEmitWho, data: CreateOpenTimeRecordDataDto, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MC1. Validate action
    await this.validateOpenTimeRecordCreation(data, transaction);

    // MC2. Build model
    const recordToCreate = await this.buildOpenTimeRecordDto(data as OpenTimeRecordDataDto, data as BaseTimeRecordDataDto, null, transaction);

    // MC3. Idempotent check
    const existingRecord = await this.getOpenTimeRecord(data.UserId, data.ClockIn, transaction);
    if (existingRecord != null && this.checkCoreFieldsMatch(recordToCreate, existingRecord)) {
      return existingRecord.RecordId;
    }

    // MC4. Low-level create
    const createdRecordId = await this.createTimeRecord(who, recordToCreate, transaction);

    // MC5. Return id
    return createdRecordId;
  }

  async createClosedTimeRecord(who: DataChangeEmitWho, data: CreateClosedTimeRecordDataDto, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MC1. Validate action
    await this.validateClosedTimeRecordCreation(data, transaction);

    // MC2. Build model
    const recordToCreate = await this.buildClosedTimeRecordDto(data as CloseTimeRecordDataDto, data as CreateOpenTimeRecordDataDto, null, transaction);

    // MC3. Idempotent check
    const existingRecord = (await this.getTimeRecordsByUserIdProjectIdDate(data.UserId, data.ProjectId, data.ClockIn, transaction)).find(existingRecord =>
      this.checkCoreFieldsMatch(recordToCreate, existingRecord)
    );
    if (existingRecord != null) {
      return existingRecord.RecordId;
    }

    // MC4. Low-level create
    const createdRecordId = await this.createTimeRecord(who, recordToCreate, transaction);

    // MC5. Return id
    return createdRecordId;
  }

  // #endregion
  // #region Mid-level Update

  async updateTimeRecordClocks(
    who: DataChangeEmitWho,
    recordId: number,
    data: UpdateTimeRecordClocksDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    let existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateClocksUpdate(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    if (await this.checkIsTimeRecordSubmitted(existingRecord, transaction)) {
      await this.unsubmitTimeRecord(who, recordId, data, skipLocalCopyValidation, localCopy, transaction);
      existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);
    }

    const recordToUpdate = existingRecord.clone();
    if (data?.ClockIn != existingRecord.ClockIn || data?.ClockInTimeZoneId != existingRecord.ClockInTimeZoneId) {
      recordToUpdate.setClockInFields(data.ClockIn, data.ClockInTimeZoneId, data.CapturedDateTime, data.CapturedDateTimeTimeZoneId);
    }
    if (data?.ClockOut != existingRecord.ClockOut || data?.ClockOutTimeZoneId != existingRecord.ClockOutTimeZoneId) {
      recordToUpdate.setClockOutFields(data.ClockOut, data.ClockOutTimeZoneId, data.CapturedDateTime, data.CapturedDateTimeTimeZoneId);
    }

    if (await this.checkIsTimeRecordOpen(recordToUpdate, transaction)) {
      await this.validateOpenTimeRecord(recordToUpdate, true, transaction);
    } else {
      await this.validateClosedTimeRecord(recordToUpdate, true, transaction);
    }

    // MU4. Idempotent check
    if (this.checkClockFieldsMatch(existingRecord, recordToUpdate)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'ClockTimes', function: this.checkClockFieldsMatch }];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async updateTimeRecordAssignment(
    who: DataChangeEmitWho,
    recordId: number,
    data: UpdateTimeRecordAssignmentDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    let existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateAssignmentUpdate(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    if (await this.checkIsTimeRecordSubmitted(existingRecord, transaction)) {
      await this.unsubmitTimeRecord(who, recordId, data, skipLocalCopyValidation, localCopy, transaction);
      existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);
    }

    const recordToUpdate = existingRecord.clone();
    recordToUpdate.setAssignmentFields(data.ProjectId, data.TaskId, data.LaborCategoryId, data.FIPSStateId, data.ActivityLogRecordId, data.ActivityLogId);

    // MU4. Idempotent check
    if (this.checkAssignmentFieldsMatch(existingRecord, recordToUpdate)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'AssignmentFields', function: this.checkAssignmentFieldsMatch }];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async unsubmitTimeRecord(who: DataChangeEmitWho, recordId: number, data: {}, skipLocalCopyValidation: boolean, localCopy: TimeRecord | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateTimeRecordUnsubmit(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const closeTimeRecordData = {
      ClockOut: existingRecord.ClockOut,
      ClockOutTimeZoneId: existingRecord.ClockOutTimeZoneId,
      CapturedClockOut: existingRecord.CapturedClockOut,
      CapturedClockOutTimeZoneId: existingRecord.CapturedClockOutTimeZoneId,
    };
    const recordToUpdate = await this.buildClosedTimeRecordDto(closeTimeRecordData, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (!(await this.checkIsTimeRecordSubmitted(existingRecord, transaction))) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async reopenTimeRecord(
    who: DataChangeEmitWho,
    recordId: number,
    data: OpenTimeRecordDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateTimeRecordReopen(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildOpenTimeRecordDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsTimeRecordOpen(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'ClockTimes', function: this.checkClockFieldsMatch }];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async closeTimeRecord(
    who: DataChangeEmitWho,
    recordId: number,
    data: CloseTimeRecordDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateTimeRecordClosure(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildClosedTimeRecordDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsTimeRecordClosed(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'ClockTimes', function: this.checkClockFieldsMatch }];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async returnTimeRecord(
    who: DataChangeEmitWho,
    recordId: number,
    data: ReturnTimeRecordDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateTimeRecordReturn(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildReturnedTimeRecordDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsTimeRecordReturned(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'CoreFields', function: this.checkCoreFieldsMatch.bind(this) }];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async submitTimeRecord(
    who: DataChangeEmitWho,
    recordId: number,
    data: SubmitTimeRecordDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateTimeRecordSubmission(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildSubmittedTimeRecordDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsTimeRecordSubmitted(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'ClockFields', function: this.checkClockFieldsMatch }];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async verifyTimeRecord(
    who: DataChangeEmitWho,
    recordId: number,
    data: VerifyTimeRecordDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    const existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateTimeRecordVerification(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    const recordToUpdate = await this.buildVerifiedTimeRecordDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsTimeRecordVerified(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'CoreFields', function: this.checkCoreFieldsMatch.bind(this) }];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  async approveTimeRecord(
    who: DataChangeEmitWho,
    recordId: number,
    data: ApproveTimeRecordDataDto,
    skipLocalCopyValidation: boolean,
    localCopy: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // MU1. Get existing record
    let existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);

    // MU2. Validate action
    await this.validateTimeRecordApproval(data, existingRecord, true, transaction);

    // MU3. Prepare changes
    if (data.VerifyWhileApproving) {
      await this.verifyTimeRecord(who, recordId, { VerifiedDate: data.ApprovedDate, VerifiedTimeZoneId: data.ApprovedTimeZoneId }, skipLocalCopyValidation, localCopy, transaction);
      existingRecord = await this.getTimeRecordByRecordId(recordId, transaction);
    }
    const recordToUpdate = await this.buildApprovedTimeRecordDto(data, null, existingRecord, transaction);

    // MU4. Idempotent check
    if (await this.checkIsTimeRecordApproved(existingRecord, transaction)) {
      return;
    }

    // MU5. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<TimeRecord>[] = [{ name: 'CoreFields', function: this.checkCoreFieldsMatch.bind(this) }];
      EntityHelper.validateLocalCopy<TimeRecord>(localCopy, existingRecord, checks);
    }

    // MU6. Low-level update
    await this.updateTimeRecord(who, recordToUpdate, skipLocalCopyValidation, localCopy, transaction);

    // MU7. Return
    return;
  }

  // #endregion
  // #region Mid-level Delete

  // #endregion
  // #region Mid-level Validate Action

  async validateClocksUpdate(data: UpdateTimeRecordClocksDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim

    if ((await this.checkIsTimeRecordOpen(existingRecord, transaction)) && (data?.ClockOut || data?.ClockOutTimeZoneId)) {
      throw new ActionValidationException('set clock out of', TimeRecordConstants.FRIENDLY_NAME, 'the time record is open.');
    }
  }

  async validateAssignmentUpdate(data: UpdateTimeRecordAssignmentDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateOpenTimeRecordCreation(data: CreateOpenTimeRecordDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateClosedTimeRecordCreation(data: CreateClosedTimeRecordDataDto, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateTimeRecordUnsubmit(data: OpenTimeRecordDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateTimeRecordReopen(data: OpenTimeRecordDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateTimeRecordClosure(data: CloseTimeRecordDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateTimeRecordSubmission(data: SubmitTimeRecordDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateTimeRecordVerification(data: VerifyTimeRecordDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (this.checkTimeRecordIsUsersOwn(existingRecord)) {
      throw new ValidationException("User cannot perform this action on their own time record.");
    }

    if (!this.roleClaimService.checkHasVerifyPermission()) {
      throw new PermissionException('User does not have permission to verify time records.');
    }

    if (!(await this.checkIsTimeRecordClosed(existingRecord, transaction))) {
      throw new StateValidationException('Closed', existingRecord);
    }
  }

  async validateTimeRecordApproval(data: ApproveTimeRecordDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (this.checkTimeRecordIsUsersOwn(existingRecord)) {
      throw new ValidationException("User cannot perform this action on their own time record.");
    }

    if (!this.roleClaimService.checkHasApprovePermission()) {
      throw new PermissionException('User does not have permission to approve time records.');
    }

    if (!skipLowLevelValidation) {
      if (data.VerifyWhileApproving) {
        const verifyData: VerifyTimeRecordDataDto = {
          VerifiedDate: data.ApprovedDate,
          VerifiedTimeZoneId: data.ApprovedTimeZoneId,
        };
        await this.validateTimeRecordVerification(verifyData, existingRecord, true, transaction);
      }
    }

    if (!data.VerifyWhileApproving && !(await this.checkIsTimeRecordVerified(existingRecord, transaction))) {
      throw new ActionValidationException('approve', TimeRecordConstants.FRIENDLY_NAME, 'it has not been verified');
    }
  }

  async validateTimeRecordLocking(data: LockTimeRecordDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    // TODO: Check role claim
  }

  async validateTimeRecordReturn(data: ReturnTimeRecordDataDto, existingRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    
    if (this.checkTimeRecordIsUsersOwn(existingRecord)) {
      throw new ValidationException("User cannot perform this action on their own time record.");
    }

    if (!(await this.checkIsTimeRecordClosed(existingRecord, transaction))) {
      throw new StateValidationException('Closed', existingRecord);
    }
  }

  // #endregion
  // #region Mid-level Validate Model

  async validateOpenTimeRecord(timeRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateTimeRecord(timeRecord, transaction);
    }

    if (!(await this.checkIsTimeRecordOpen(timeRecord, transaction))) {
      throw new StateValidationException('Open', timeRecord);
    }
  }

  async validateClosedTimeRecord(timeRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateTimeRecord(timeRecord, transaction);
    }

    if (!(await this.checkIsTimeRecordClosed(timeRecord, transaction))) {
      throw new StateValidationException('Closed', timeRecord);
    }

    if (!this.checkTimeRecordDatesSameDay(timeRecord)) {
      throw new ValidationException('All dates must be on the same day.');
    }

    if (!this.checkTimeRecordStartBeforeEnd(timeRecord)) {
      throw new ValidationException('Start time must be before end time.');
    }
  }

  async validateReturnedTimeRecord(timeRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateTimeRecord(timeRecord, transaction);
    }

    if (!(await this.checkIsTimeRecordReturned(timeRecord, transaction))) {
      throw new StateValidationException('Returned', timeRecord);
    }
  }

  async validateSubmittedTimeRecord(timeRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateTimeRecord(timeRecord, transaction);
    }

    if (!(await this.checkIsTimeRecordSubmitted(timeRecord, transaction))) {
      throw new StateValidationException('Submitted', timeRecord);
    }
  }

  async validateVerifiedTimeRecord(timeRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateTimeRecord(timeRecord, transaction);
    }

    if (!(await this.checkIsTimeRecordVerified(timeRecord, transaction))) {
      throw new StateValidationException('Verified', timeRecord);
    }
  }

  async validateApprovedTimeRecord(timeRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateTimeRecord(timeRecord, transaction);
    }

    if (!(await this.checkIsTimeRecordApproved(timeRecord, transaction))) {
      throw new StateValidationException('Approved', timeRecord);
    }
  }

  async validateLockedTimeRecord(timeRecord: TimeRecord, skipLowLevelValidation: boolean, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (!skipLowLevelValidation) {
      await this.validateTimeRecord(timeRecord, transaction);
    }

    if (!(await this.checkIsTimeRecordLocked(timeRecord, transaction))) {
      throw new StateValidationException('Locked', timeRecord);
    }
  }

  // #endregion
  // #region Mid-level Upsert

  // #endregion
  // #region Mid-level Sync

  async updateTimeRecordWithId(who: DataChangeEmitWho, recordId: number, onlineId: number, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    let recordToUpdate = await this.getTimeRecordByRecordId(recordId, transaction, true);
    recordToUpdate.Id = onlineId;

    await this.updateTimeRecord(who, recordToUpdate, true, null, transaction);
  }

  async upsertTimeRecordFromSync(who: DataChangeEmitWho, recordToUpsert: TimeRecord, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getTimeRecordByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateTimeRecord(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createTimeRecord(who, recordToUpsert, transaction, true);
      }
    }
    return;
  }

  // #endregion
  // #region Build

  private async buildBaseTimeRecordDto(data: BaseTimeRecordDataDto, existingTransaction?: DbTransaction): Promise<TimeRecord> {
    return new TimeRecord({
      EmployeeId: data.UserId,
      EmployeeNumber: data.EmployeeNumber,
      Activity: data.Activity,
      ProjectId: data.ProjectId,
      TaskId: data.TaskId,
      LaborCategoryId: data.LaborCategoryId,
      FIPSStateId: data.FIPSStateId,
      ActivityLogRecordId: data.ActivityLogRecordId,
      OrganizationId: data.OrganizationId,
      ClockIn: DateTimeHelper.formatDateTime(data.ClockIn),
      ClockInOffset: DateTimeHelper.getUtcOffsetInMinutes(data.ClockIn, data.ClockInTimeZoneId),
      ClockInTimeZoneId: data.ClockInTimeZoneId,
      CapturedClockIn: DateTimeHelper.formatDateTime(data.CapturedClockIn),
      CapturedClockInOffset: DateTimeHelper.getUtcOffsetInMinutes(data.CapturedClockIn, data.CapturedClockInTimeZoneId),
      CapturedClockInTimeZoneId: data.CapturedClockInTimeZoneId,
      InputType: data.InputType,
      StatusId: 0,
      IsDeleted: false,
      IsArchived: false,
    });
  }

  async buildOpenTimeRecordDto(data: OpenTimeRecordDataDto, baseData: BaseTimeRecordDataDto | null, existingRecord: TimeRecord | null, existingTransaction?: DbTransaction): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildBaseTimeRecordDto(baseData as BaseTimeRecordDataDto, transaction));

    const openStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.Open, transaction);

    record.setToOpen(openStatus);

    await this.validateOpenTimeRecord(record, true, transaction);

    return record;
  }

  async buildClosedTimeRecordDto(
    data: CloseTimeRecordDataDto,
    baseData: CreateOpenTimeRecordDataDto | null,
    existingRecord: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildOpenTimeRecordDto(baseData as OpenTimeRecordDataDto, baseData, null, transaction));

    const pendingOverrideStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.PendingOverride, transaction);
    const pendingVerificationStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.PendingVerification, transaction);
    record.setToClosed(pendingOverrideStatus, pendingVerificationStatus, data.ClockOut, data.ClockOutTimeZoneId, data.CapturedClockOut, data.CapturedClockOutTimeZoneId);

    await this.validateClosedTimeRecord(record, true, transaction);

    return record;
  }

  async buildReturnedTimeRecordDto(
    data: ReturnTimeRecordDataDto,
    baseData: CreateClosedTimeRecordDataDto | null,
    existingRecord: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildClosedTimeRecordDto(baseData as CloseTimeRecordDataDto, baseData, null, transaction));

    // TODO: Replace with Returned status after it is created
    const returnedStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.Returned, transaction);
    record.setToReturned(returnedStatus, this.userService.getCurrentUserId(), data.ReturnedDate, data.ReturnedTimeZoneId);

    await this.validateReturnedTimeRecord(record, true, transaction);

    return record;
  }

  async buildSubmittedTimeRecordDto(
    data: SubmitTimeRecordDataDto,
    baseData: CreateClosedTimeRecordDataDto | null,
    existingRecord: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildClosedTimeRecordDto(baseData as CloseTimeRecordDataDto, baseData, null, transaction));

    // TODO: Replace with Submitted status after it is created
    const pendingOverrideStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.PendingOverride, transaction);
    const pendingVerificationStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.PendingVerification, transaction);

    record.setToSubmitted(pendingOverrideStatus, pendingVerificationStatus, this.userService.getCurrentUserId(), data.SubmittedDate, data.SubmittedTimeZoneId);

    await this.validateSubmittedTimeRecord(record, true, transaction);

    return record;
  }

  async buildVerifiedTimeRecordDto(
    data: VerifyTimeRecordDataDto,
    baseData: CreateSubmittedTimeRecordDataDto | null,
    existingRecord: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildSubmittedTimeRecordDto(baseData as SubmitTimeRecordDataDto, baseData, null, transaction));

    const verifiedStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.Verified, transaction);
    record.setToVerified(verifiedStatus, this.userService.getCurrentUserId(), data.VerifiedDate, data.VerifiedTimeZoneId);

    await this.validateVerifiedTimeRecord(record, true, transaction);

    return record;
  }

  async buildApprovedTimeRecordDto(
    data: ApproveTimeRecordDataDto,
    baseData: CreateVerifiedTimeRecordDataDto | null,
    existingRecord: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildVerifiedTimeRecordDto(baseData as VerifyActivityLogDataDto, baseData, null, transaction));

    const approvedStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.Approved, transaction);
    record.setToApproved(approvedStatus, this.userService.getCurrentUserId(), data.ApprovedDate, data.ApprovedTimeZoneId);

    await this.validateApprovedTimeRecord(record, true, transaction);

    return record;
  }

  async buildLockedTimeRecordDto(
    data: LockTimeRecordDataDto,
    baseData: CreateApprovedTimeRecordDataDto | null,
    existingRecord: TimeRecord | null,
    existingTransaction?: DbTransaction
  ): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    if (!baseData && !existingRecord) {
      throw new Error('baseData and existingRecord cannot both be null.');
    }

    const record = existingRecord?.clone() || (await this.buildApprovedTimeRecordDto(baseData as ApproveTimeRecordDataDto, baseData, null, transaction));

    const lockedStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.Locked, transaction);
    record.setToLocked(lockedStatus, this.userService.getCurrentUserId(), data.LockedDate, data.LockedTimeZoneId);

    await this.validateLockedTimeRecord(record, true, transaction);

    return record;
  }

  // #endregion
  // #region Check

  async checkIsTimeRecordOpen(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const status = await this.timeRecordStatusService.getTimeRecordStatusByOnlineId(timeRecord.StatusId, transaction);
    return TimeRecordStatusHierarchy.OpenStatusNumbers.includes(status.Number);
  }

  async checkIsTimeRecordClosed(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const status = await this.timeRecordStatusService.getTimeRecordStatusByOnlineId(timeRecord.StatusId, transaction);
    return TimeRecordStatusHierarchy.ClosedStatusNumbers.includes(status.Number);
  }

  async checkIsTimeRecordReturned(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const status = await this.timeRecordStatusService.getTimeRecordStatusByOnlineId(timeRecord.StatusId, transaction);
    return TimeRecordStatusHierarchy.ReturnedStatusNumbers.includes(status.Number);
  }

  async checkIsTimeRecordSubmitted(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const status = await this.timeRecordStatusService.getTimeRecordStatusByOnlineId(timeRecord.StatusId, transaction);
    return TimeRecordStatusHierarchy.SubmittedStatusNumbers.includes(status.Number);
  }

  async checkIsTimeRecordVerified(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const status = await this.timeRecordStatusService.getTimeRecordStatusByOnlineId(timeRecord.StatusId, transaction);
    return TimeRecordStatusHierarchy.VerifiedStatusNumbers.includes(status.Number);
  }

  async checkIsTimeRecordApproved(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const status = await this.timeRecordStatusService.getTimeRecordStatusByOnlineId(timeRecord.StatusId, transaction);
    return TimeRecordStatusHierarchy.ApprovedStatusNumbers.includes(status.Number);
  }

  async checkIsTimeRecordLocked(timeRecord: TimeRecord, existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const status = await this.timeRecordStatusService.getTimeRecordStatusByOnlineId(timeRecord.StatusId, transaction);
    return TimeRecordStatusHierarchy.LockedStatusNumbers.includes(status.Number);
  }

  checkIsTimeRecordSoftDeleted(timeRecord: TimeRecord): boolean {
    return timeRecord.IsDeleted;
  }

  checkPendingOverrideConditions(timeRecord: TimeRecord): boolean {
    return timeRecord.hasOverrideCondition();
  }

  checkTimeRecordDatesSameDay(timeRecord: TimeRecord): boolean {
    return DateTimeHelper.checkOnSameDay(timeRecord.ClockIn, timeRecord.ClockOut!, true);
  }

  checkTimeRecordStartBeforeEnd(timeRecord: TimeRecord): boolean {
    return DateTimeHelper.checkTimeIsBefore(timeRecord.ClockIn, timeRecord.ClockInTimeZoneId, timeRecord.ClockOut!, timeRecord.ClockOutTimeZoneId!);
  }

  checkTimeRecordEndAfterStart(timeRecord: TimeRecord): boolean {
    return DateTimeHelper.checkTimeIsAfter(timeRecord.ClockOut!, timeRecord.ClockOutTimeZoneId!, timeRecord.ClockIn, timeRecord.ClockInTimeZoneId);
  }

  private checkImmutableFieldsMatch(a: TimeRecord, b: TimeRecord): boolean {
    return a.EmployeeId === b.EmployeeId && a.Activity === b.Activity && a.OrganizationId === b.OrganizationId;
  }

  private checkCoreFieldsMatch(a: TimeRecord, b: TimeRecord): boolean {
    return this.checkImmutableFieldsMatch(a, b) && this.checkClockFieldsMatch(a, b) && this.checkAssignmentFieldsMatch(a, b);
  }

  private checkClockFieldsMatch(a: TimeRecord, b: TimeRecord): boolean {
    return (
      a.ClockIn === b.ClockIn &&
      a.ClockInOffset === b.ClockInOffset &&
      a.ClockInTimeZoneId === b.ClockInTimeZoneId &&
      a.ClockOut === b.ClockOut &&
      a.ClockOutOffset === b.ClockOutOffset &&
      a.ClockOutTimeZoneId === b.ClockOutTimeZoneId
    );
  }

  private checkAssignmentFieldsMatch(a: TimeRecord, b: TimeRecord): boolean {
    return a.ActivityLogId === b.ActivityLogId && a.ProjectId === b.ProjectId && a.TaskId === b.TaskId && a.LaborCategoryId === b.LaborCategoryId && a.FIPSStateId === b.FIPSStateId;
  }

  checkTimeRecordsOverlap(a: TimeRecord, b: TimeRecord): boolean {
    const aClockOut = a.ClockOut ?? DateTimeHelper.getEndOfDay(a.ClockIn);
    const aClockOutTimeZoneId = a.ClockOutTimeZoneId ?? a.ClockInTimeZoneId;

    const bClockOut = b.ClockOut ?? DateTimeHelper.getEndOfDay(b.ClockIn);
    const bClockOutTimeZoneId = b.ClockOutTimeZoneId ?? b.ClockInTimeZoneId;

    return DateTimeHelper.checkTimeRangesOverlap(a.ClockIn, a.ClockInTimeZoneId, aClockOut, aClockOutTimeZoneId, b.ClockIn, b.ClockInTimeZoneId, bClockOut, bClockOutTimeZoneId, false);
  }

  checkDatetimeInTimeRecord(timeRecord: TimeRecord, targetDatetime: DateTimeType, targetTimeZoneId: string): boolean {
    if (timeRecord.ClockOut) {
      return (
        DateTimeHelper.checkTimeIsBetween(targetDatetime, targetTimeZoneId, timeRecord.ClockIn, timeRecord.ClockInTimeZoneId, timeRecord.ClockOut, timeRecord.ClockOutTimeZoneId, true) &&
        !DateTimeHelper.checkTimeIsEqual(targetDatetime, targetTimeZoneId, timeRecord.ClockOut, timeRecord.ClockOutTimeZoneId)
      );
    } else {
      return DateTimeHelper.checkTimeIsAfter(targetDatetime, targetTimeZoneId, timeRecord.ClockIn, timeRecord.ClockInTimeZoneId, true);
    }
  }

  async checkContainsOpenTimeRecord(timeRecords: TimeRecord[], existingTransaction?: DbTransaction): Promise<boolean> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const checkIsOpenPromises = timeRecords.map(async timeRecord => await this.checkIsTimeRecordOpen(timeRecord, transaction));
    const results = await Promise.all(checkIsOpenPromises);

    return results.some(result => result);
  }

  checkIsTimeRecordWorking(timeRecord: TimeRecord): boolean {
    return timeRecord.Activity === TimeRecordActivity.Working;
  }

  checkIsTimeRecordBreak(timeRecord: TimeRecord): boolean {
    return timeRecord.Activity === TimeRecordActivity.Meal || timeRecord.Activity === TimeRecordActivity.Rest;
  }

  checkIsTimeRecordMealBreak(timeRecord: TimeRecord): boolean {
    return timeRecord.Activity === TimeRecordActivity.Meal;
  }

  checkIsTimeRecordRestBreak(timeRecord: TimeRecord): boolean {
    return timeRecord.Activity === TimeRecordActivity.Rest;
  }

  checkIsAnyTimeRecordBreak(timeRecords: TimeRecord[]): boolean {
    return timeRecords.some(timeRecord => this.checkIsTimeRecordBreak(timeRecord));
  }

  checkAnyTimeRecordIdentifiersMatch(a: TimeRecord, b: TimeRecord): boolean {
    if (!a?.RecordId && !a?.Id) {
      return false;
    }

    if (!b?.RecordId && !b?.Id) {
      return false;
    }

    return a?.RecordId === b?.RecordId || a?.Id === b?.Id;
  }

  checkTimeRecordIsUsersOwn(timeRecord: TimeRecord): boolean {
    return timeRecord.EmployeeId === this.userService.getCurrentUserId();
  }

  private checkRestBreakDurationBelowMax(timeRecord: TimeRecord): boolean {
    if (!this.checkIsTimeRecordRestBreak(timeRecord)) {
      return true; // Not a rest break, skip check
    }

    const duration = this.getDurationForTimeRecords([timeRecord]);
    return duration.asMinutes() <= TimeRecordConstants.REST_BREAK_DURATION_MINUTES;
  }

  // #endregion
  // #region Lookup

  // #endregion
  // #region Transform

  filterForUniqueTimeRecords(timeRecords: TimeRecord[]): TimeRecord[] {
    return timeRecords.filter((e, i, arr) => arr.findIndex(a => (a['Id'] && e['Id'] && a['Id'] === e['Id']) || (a['RecordId'] && e['RecordId'] && a['RecordId'] === e['RecordId'])) === i);
  }

  getDurationForTimeRecords(timeRecords: TimeRecord[], intervalMinutes: number = GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES): plugin.Duration {
    const intervals = timeRecords.map(() => intervalMinutes);
    return this.getDurationForTimeRecordsWithIntervals(timeRecords, intervals);
  }

  getDurationForPaidTimeRecords(timeRecords: TimeRecord[], intervalMinutes: number = GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES): plugin.Duration {
    let workingTimeRecords = timeRecords.filter(timeRecord => timeRecord.Activity === TimeRecordActivity.Working || timeRecord.Activity === TimeRecordActivity.Rest);
    return this.getDurationForTimeRecords(workingTimeRecords, intervalMinutes);
  }

  getDurationForMealBreakTimeRecords(timeRecords: TimeRecord[], intervalMinutes: number = GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES): plugin.Duration {
    let mealBreakTimeRecords = timeRecords.filter(timeRecord => timeRecord.Activity === TimeRecordActivity.Meal);
    return this.getDurationForTimeRecords(mealBreakTimeRecords, intervalMinutes);
  }

  async warmIntervalCaches(timeRecords: TimeRecord[]): Promise<void> {
    const uniqueEmployeeIds = [...new Set(timeRecords.map(r => r.EmployeeId).filter(id => id != null))];
    const uniqueFipsStateIds = [...new Set(timeRecords.map(r => r.FIPSStateId).filter(id => id != null))];

    await Promise.all(uniqueEmployeeIds.map(async employeeId => {
      const user = await this.userService.getUserByOnlineId(employeeId);
      this.isExemptCacheByEmployeeId.set(employeeId, user?.IsExempt ?? true);
    }));

    await Promise.all(uniqueFipsStateIds.map(async fipsStateId => {
      if (!this.intervalCacheByFipsStateId.has(fipsStateId)) {
        const fipsState = await this.lookupService.getFipsStateById(fipsStateId);
        const interval = fipsState?.TimeRoundToMinutes === true ? 1 : GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
        this.intervalCacheByFipsStateId.set(fipsStateId, interval);
      }
    }));
  }

  getIntervalForTimeRecord(timeRecord: TimeRecord): number {
    if (!timeRecord?.FIPSStateId) {
      return GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
    }
    const isExempt = this.isExemptCacheByEmployeeId.get(timeRecord.EmployeeId) ?? true;
    if (isExempt) {
      return GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
    }
    return this.intervalCacheByFipsStateId.get(timeRecord.FIPSStateId) ?? GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
  }

  getDurationForTimeRecordsPerRecordInterval(timeRecords: TimeRecord[]): plugin.Duration {
    const perRecordIntervals = timeRecords.map(timeRecord => {
      if (!timeRecord?.FIPSStateId) {
        return GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
      }
      const isExempt = this.isExemptCacheByEmployeeId.get(timeRecord.EmployeeId) ?? true;
      if (isExempt) {
        return GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
      }
      return this.intervalCacheByFipsStateId.get(timeRecord.FIPSStateId)
        ?? GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
    });
    return this.getDurationForTimeRecordsWithIntervals(timeRecords, perRecordIntervals);
  }

  getDurationForPaidTimeRecordsPerRecordInterval(timeRecords: TimeRecord[]): plugin.Duration {
    const workingTimeRecords = timeRecords.filter(timeRecord => timeRecord.Activity === TimeRecordActivity.Working || timeRecord.Activity === TimeRecordActivity.Rest);
    return this.getDurationForTimeRecordsPerRecordInterval(workingTimeRecords);
  }

  getDurationForMealBreakTimeRecordsPerRecordInterval(timeRecords: TimeRecord[]): plugin.Duration {
    const mealBreakTimeRecords = timeRecords.filter(timeRecord => timeRecord.Activity === TimeRecordActivity.Meal);
    return this.getDurationForTimeRecordsPerRecordInterval(mealBreakTimeRecords);
  }

  private getDurationForTimeRecordsWithIntervals(timeRecords: TimeRecord[], intervalMinutesPerRecord: number[]): plugin.Duration {
    let sumDuration = 0;

    for (let i = 0; i < timeRecords.length; i++) {
      const timeRecord = timeRecords[i];
      const intervalMinutes = intervalMinutesPerRecord[i] ?? GlobalConstants.DEFAULT_TIMECLOCK_INTERVAL_MINUTES;
      sumDuration += this.getTimeRecordDurationMs(timeRecord, intervalMinutes);
    }

    return dayjs.duration(sumDuration);
  }

  private getTimeRecordDurationMs(timeRecord: TimeRecord, intervalMinutes: number): number {
    let clockOut;

    if (timeRecord.ClockOut) {
      //closed time records
      clockOut = dayjs.tz(timeRecord.ClockOut, timeRecord.ClockOutTimeZoneId);
    } else if (dayjs.tz(timeRecord.ClockIn, timeRecord.ClockInTimeZoneId).isToday()) {
      //today's open time record
      clockOut = dayjs();
    } else {
      //time record from previous day where user forgot to clock out
      clockOut = dayjs.tz(timeRecord.ClockIn, timeRecord.ClockOutTimeZoneId).endOf('day');
    }

    const diff = dayjs(clockOut).diff(dayjs.tz(timeRecord.ClockIn, timeRecord.ClockInTimeZoneId));
    if (intervalMinutes <= 1) {
      return diff;
    }

    return Math.round(diff / (intervalMinutes * 60000)) * (intervalMinutes * 60000);
  }

  async getLaborCategoryNameForTimeRecord(timeRecord: TimeRecord): Promise<string> {
    return await this.laborCategoryService.getLaborCategoryNameById(timeRecord.LaborCategoryId);
  }

  async getFieldActivityNameForTimeRecord(timeRecord: TimeRecord): Promise<string> {
    return await this.projectService.getFieldActivityName(undefined, timeRecord.EmployeeId, timeRecord.ProjectId, timeRecord.TaskId, timeRecord.LaborCategoryId);
  }

  filterForLatestTimeRecord(timeRecords: TimeRecord[]): TimeRecord {
    let lastestTimeRecord = timeRecords[0];
    for (const timeRecord of timeRecords) {
      if (DateTimeHelper.checkTimeIsBefore(lastestTimeRecord.ClockIn, lastestTimeRecord.ClockInTimeZoneId, timeRecord.ClockIn, timeRecord.ClockInTimeZoneId)) {
        lastestTimeRecord = timeRecord;
      }
    }
    return lastestTimeRecord;
  }

  // #endregion
  // #region Other

  async checkIsClockedIn(userId: number): Promise<boolean> {
    const localNow = DateTimeHelper.getLocalNow();
    const lastTimeRecord = await this.getOpenTimeRecord(userId, localNow);

    if (lastTimeRecord && lastTimeRecord.Activity === TimeRecordActivity.Working) {
      return true;
    } else {
      return false;
    }
  }

  async checkIsOnBreak(userId: number): Promise<boolean> {
    const localNow = DateTimeHelper.getLocalNow();
    const openTimeRecord = await this.getOpenTimeRecord(userId, localNow);

    if (openTimeRecord && this.checkIsTimeRecordBreak(openTimeRecord)) {
      return true;
    } else {
      return false;
    }
  }

  async checkIsOnRestBreak(userId: number): Promise<boolean> {
    const localNow = DateTimeHelper.getLocalNow();
    const openTimeRecord = await this.getOpenTimeRecord(userId, localNow);

    if (openTimeRecord && this.checkIsTimeRecordRestBreak(openTimeRecord)) {
      return true;
    } else {
      return false;
    }
  }

  async checkHasBeenLessThan15MinutesSinceRestBreakEnd(userId: number): Promise<boolean> {
    const timeRecordsForTheDay = await this.getTimeRecordsByUserIdDate(userId, DateTimeHelper.getLocalNow());
    const lastTimeRecord = this.filterForLatestTimeRecord(timeRecordsForTheDay);
    const previousTimeRecord = timeRecordsForTheDay[timeRecordsForTheDay.indexOf(lastTimeRecord) - 1];

    return (previousTimeRecord && previousTimeRecord.Activity === TimeRecordActivity.Rest && DateTimeHelper.checkTimeIsBefore(DateTimeHelper.getLocalNowString(), DateTimeHelper.getTimeZoneId(), DateTimeHelper.addTime(previousTimeRecord.ClockOut, 15, 'minute'), previousTimeRecord.ClockOutTimeZoneId));
  }

  async checkIsOnMealBreak(userId: number): Promise<boolean> {
    const localNow = DateTimeHelper.getLocalNow();
    const openTimeRecord = await this.getOpenTimeRecord(userId, localNow);

    if (openTimeRecord && this.checkIsTimeRecordMealBreak(openTimeRecord)) {
      return true;
    } else {
      return false;
    }
  }

  getLastAllowedClockInForDay(day: DateTimeType, minInterval: number): dayjs.Dayjs {
    let startOfNexDay = dayjs(day).add(1, 'day').startOf('day');
    return startOfNexDay.subtract(minInterval, 'minute');
  }

  getFirstAllowedClockOutForDay(day: DateTimeType, minInterval: number, clockIn?: DateTimeType): dayjs.Dayjs {
    // If clockIn provided, minimum clock-out is clockIn + interval
    if (clockIn) {
      return DateTimeHelper.addTime(clockIn, minInterval, 'minute');
    }
    // Otherwise, use start of day + minInterval
    const startOfDay = DateTimeHelper.getStartOfDay(day);
    return DateTimeHelper.addTime(startOfDay, minInterval, 'minute');
  }

  async getLatestClockIn(userId: number, day: DateTimeType): Promise<dayjs.Dayjs> {
    const timeRecords = await this.getTimeRecordsByUserIdDate(userId, day);
    const timeRecordsWithClockIn = timeRecords.filter(timeRecord => timeRecord.ClockIn);
    return dayjs.max(timeRecordsWithClockIn.map(timeRecord => dayjs.tz(timeRecord.ClockIn, timeRecord.ClockInTimeZoneId)));
  }

  async getLatestClockOut(userId: number, day: DateTimeType): Promise<dayjs.Dayjs> {
    const timeRecords = await this.getTimeRecordsByUserIdDate(userId, day);
    const timeRecordsWithClockOut = timeRecords.filter(timeRecord => timeRecord.ClockOut);
    return dayjs.max(timeRecordsWithClockOut.map(timeRecord => dayjs.tz(timeRecord.ClockOut, timeRecord.ClockOutTimeZoneId)));
  }

  getLastAllowedBreakStart(day: DateTimeType, minInterval: number): dayjs.Dayjs {
    return this.getLastAllowedClockInForDay(day, minInterval).subtract(minInterval, 'minute'); //user needs to be able to clock back in from break in same day
  }

  async getMinClockInForDay(day: DateTimeType, userId: number): Promise<dayjs.Dayjs> {
    let allTimeRecordsForDay = await this.getTimeRecordsByUserIdDate(userId, dayjs(day).toDate());
    let earliestTimeRecordForDay = allTimeRecordsForDay.reduce(function (prev, curr) {
      return dayjs(prev.ClockIn, prev.ClockInTimeZoneId).isBefore(dayjs(curr.ClockIn, curr.ClockInTimeZoneId)) ? prev : curr;
    });

    return dayjs.tz(earliestTimeRecordForDay.ClockIn, earliestTimeRecordForDay.ClockInTimeZoneId);
  }

  // #endregion

  // Need to update below methods to fit new pattern

  async setForgotToClockOut(timeRecordRecordId: number): Promise<TimeRecord> {
    const transaction = await this.startTransactionIfNeeded();
    let timeRecord = await this.getTimeRecordByRecordId(timeRecordRecordId, transaction);

    const pendingOverrideStatus = await this.timeRecordStatusService.getTimeRecordStatusByNumber(TimeRecordStatusNumber.PendingOverride, transaction);
    timeRecord.StatusId = pendingOverrideStatus.Id;
    timeRecord.ClockOut = dayjs(timeRecord.ClockIn).set('hour', 23).set('minute', 59).format('YYYY-MM-DDTHH:mm:00');

    await this.updateTimeRecord(DataChangeEmitWho.Self, timeRecord, true, null, transaction);
    return timeRecord;
  }
}
